import { TFunction } from 'i18next';
import { MACD as MACDCalculator } from 'technicalindicators';

import { PriceDataPoint } from '../../../types';
import {
  DataSetSeriesGenerator,
  getDefaultToolptipFormatter,
  PriceViewerSeries,
} from './base';
import { fillPriceDates } from './utils';

class MACD extends DataSetSeriesGenerator {
  isSeparateFromPriceChart = true;
  fastPeriod: number;
  slowPeriod: number;
  signalPeriod: number;

  constructor(t: TFunction) {
    super(t);
    // default values according to our old settings
    this.fastPeriod = 12;
    this.slowPeriod = 26;
    this.signalPeriod = 9;
  }

  getChartName(): string {
    return this.t('MACD');
  }

  getSeries(dataPoints: ReadonlyArray<PriceDataPoint>): PriceViewerSeries[] {
    const allDates = dataPoints.map(item => item.date.toString());
    const dataPointsWithData = dataPoints.filter(
      ({ close }) => close !== undefined
    );

    const closePrices = dataPointsWithData.map(({ close }) => close);
    const dates = dataPointsWithData.map(({ date }) => date) as string[];
    const macds = MACDCalculator.calculate({
      values: closePrices,
      fastPeriod: this.fastPeriod,
      slowPeriod: this.slowPeriod,
      signalPeriod: this.signalPeriod,
      SimpleMAOscillator: false,
      SimpleMASignal: false,
    });

    // MACD has fewer data points, need to do alignments
    const macdPadded = [
      ...new Array(dates.length - macds.length),
      ...macds.map(item => item.MACD),
    ];
    const signalPadded = [
      ...new Array(dates.length - macds.length),
      ...macds.map(item => item.signal),
    ];
    const histogramPadded = [
      ...new Array(dates.length - macds.length),
      ...macds.map(item => item.histogram),
    ];

    return [
      {
        name: `${this.t('MACD')} macd`,
        echartSeries: {
          type: 'line',
          data: fillPriceDates(allDates, macdPadded, dates),
          symbol: 'none',
          lineStyle: {
            color: 'red',
          },
        },
        tooltipFormatter: getDefaultToolptipFormatter('2d', 'number', {
          keyColor: 'red',
        }),
      },
      {
        name: `${this.t('MACD')} signal`,
        echartSeries: {
          type: 'line',
          data: fillPriceDates(allDates, signalPadded, dates),
          symbol: 'none',
          lineStyle: {
            color: 'blue',
          },
        },
        tooltipFormatter: getDefaultToolptipFormatter('2d', 'number', {
          keyColor: 'blue',
        }),
      },
      {
        name: `${this.t('MACD')} histogram`,
        echartSeries: {
          type: 'bar',
          data: fillPriceDates(allDates, histogramPadded, dates),
          symbol: 'none',
          itemStyle: {
            color: '#FDD017',
          },
        },
        tooltipFormatter: getDefaultToolptipFormatter('2d', 'number', {
          keyColor: '#FDD017',
        }),
      },
    ];
  }
}

export default MACD;
